<template>
  <section class="add-user-profile">
    <PageHeader showBreadcrumbs page-title="Add user profile">
      <template #onSideOfTitle>
        <div class="d-flex align-items-center cursor-pointer">
          <span
            v-b-tooltip.hover="{ variant: 'base', title: 'User profile will only appear on the available profiles page. It can later be converted into a regular user if needed.' }"
            class="material-icons">
            info
          </span>
        </div>
      </template>
      <template #buttons>
        <CloseButton :onClick="onClose" />
      </template>
    </PageHeader>
    <div class="block-wrapper">
      <div class="row">
        <div class="col-4">
          <div class="row">
            <div class="col-12">
              <div class="mb-3 text-center">
                <UserAvatar
                  v-model="createProfile.avatarFile"
                  :userAvatar="userAvatar"
                  @resetAvatarInputAfterModalClose="resetAvatarInputAfterModalClose"
                  @uploadCroppedAvatar="uploadCroppedAvatar"
                  @onAvatarFile="onAvatarFile"
                  />
              </div>
              <FormGenerator class="mt-4" :data="userCheckBoxes.form" :showFormControls="false"
                @updateItem="userCheckBoxesHandler" />
              <InfoBlockWrapper class="mt-2">
                <span>User profile is publicly available within your company’s available profiles at the link
                  below:</span><br />
                <a target="_blank" href="/available-profiles">{{ url }}/available-profiles</a>.
                <br /><span>To ensure a complete profile, all fields must be filled out except for the CV link.</span>
              </InfoBlockWrapper>
            </div>
          </div>
        </div>
        <div class="col-8">
          <div class="row">
            <div class="col-12">
              <PageHeader page-title="Profile details" wrapperClassName="" titleClassName="block-title" />
              <FormGenerator :data="createProfile.form" :showFormControls="false" @input="inputFormField"
                @submit="submitCreateProfileForm" ref="form" />
            </div>
            <div class="col-12">
              <div class="break-line" />
            </div>
            <div class="col-12">
              <PageHeader page-title="Contact details" titleClassName="block-title" wrapperClassName="mb-0">
                <template #buttons>
                  <b-button class="button justify-content-center chevron-button" style="height: 32px;" variant="plain"
                    @click="toggleShowingContactDetails">
                    <span class="material-symbols-outlined material-symbols-fill-1 mr-1 ml-2"
                      :style="{ transform: !userContactDetailsData.isShow ? 'rotate(180deg)' : 'none', transition: '0.2s' }">
                      keyboard_arrow_down
                    </span>
                  </b-button>
                </template>
              </PageHeader>
              <b-collapse v-model="userContactDetailsData.isShow">
                <FormGenerator class="mt-4" :data="userContactDetails.form" :showFormControls="false"
                  @submit="submitUserContactDetails" ref="contactDetailsForm" />
              </b-collapse>
            </div>
            <div class="col-12">
              <div class="break-line" />
            </div>
            <div class="col-12">
              <NewUserProfileSkills v-model="userSkills" @update="updateUserSkills"
                :showSkillsError="showSkillsError" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <StickyButtonsWrapper isShowAnimation :loading="loading" v-if="isShowStickyButtons">
      <b-button class="button title-container__controls-item justify-content-center min-width-130" variant="outline"
        @click="onClose">
        Cancel
      </b-button>
      <b-button class="button justify-content-center min-width-190" variant="primary" @click="submit">
        Submit
      </b-button>
    </StickyButtonsWrapper>
  </section>
</template>

<script>
import PageHeader from '@components/PageHeader.vue'
import UserMixin from '@mixins/UserMixin'
import FormGenerator from '@components/FormGenerator'
import NewUserProfileSkills from '@components/NewUserProfileSkills'
import UserAvatar from '@components/UserAvatar.vue'
import StickyButtonsWrapper from '@components/forms/StickyButtonsWrapper'
import InfoBlockWrapper from '@/components/common/InfoBlockWrapper'
import CloseButton from '@components/common/CloseButton.vue'
import UserContactDetailsMixin from '@mixins/UserContactDetailsMixin'

export default {
  name: 'NewUserProfile',
  mixins: [UserMixin, UserContactDetailsMixin],
  emits: ['close', 'loadingChange'],
  components: { PageHeader, FormGenerator, NewUserProfileSkills, UserAvatar, StickyButtonsWrapper, InfoBlockWrapper, CloseButton },
  props: {
    isPage: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      userAvatar: '',
      loading: false,
      userCheckBoxes: {
        form: {
          show_on_widget: {
            type: 'checkbox',
            name: 'show_on_widget',
            label: 'Show on ',
            value: false,
            class: 'col-12 user-profile-checkbox-wrapper',
            info: 'Enabling this will make this user’s profile visible on your available profiles page.',
            labelLink: ' available profiles',
            labelLinkTo: '/available-profiles'
          },
          is_contact_person: {
            type: 'checkbox',
            name: 'is_contact_person',
            label: 'Contact person',
            value: false,
            class: 'col-12 user-profile-checkbox-wrapper user-profile-checkbox-wrapper__contact'
          }
        }
      },
      userSkills: [],
      showSkillsError: false,
      isShowStickyButtons: true,
      url: window.location.hostname,
      userContactDetailsData: {
        isSubmitted: false,
        data: null,
        isShow: true
      },
      createProfileData: {
        isSubmitted: false,
        data: null
      }
    }
  },
  computed: {
    showAvatarCropModal: {
      get () {
        return this.createProfile.showAvatarCropModal
      },
      set (val) {
        this.createProfile.showAvatarCropModal = val
      }
    }
  },
  created () {
    this.updateValidationRules([{ key: 'email', validation: [{ name: 'required' }, { name: 'email' }] }])
  },
  methods: {
    // Override fetchAllData from UserMixin to add loading state management
    async fetchAllData () {
      this.loading = true
      this.$emit('loadingChange', true)

      try {
        // Call the parent fetchAllData method from UserMixin
        await this.$options.mixins.find(mixin => mixin.methods?.fetchAllData)?.methods.fetchAllData.call(this)
      } catch (error) {
        console.error('NewUserProfile: Error in fetchAllData:', error)
        throw error
      } finally {
        this.loading = false
        this.$emit('loadingChange', false)
      }
    },
    onAvatarFile (e) {
      this.handleAvatarFile(e, 'createProfile')
    },
    toggleShowingContactDetails () {
      this.userContactDetailsData = { ...this.userContactDetailsData, isShow: !this.userContactDetailsData.isShow }
    },
    submitUserContactDetails (data) {
      this.userContactDetailsData = {
        ...this.userContactDetailsData,
        isSubmitted: true,
        data
      }
    },
    submitCreateProfileForm (data) {
      this.createProfileData = {
        isSubmitted: true,
        data
      }
    },
    updateUserSkills (data) {
      this.userSkills = data
    },
    onClose () {
      if (!this.isPage) {
        this.isShowStickyButtons = false
        setTimeout(() => {
          this.$emit('close')
        })
      } else {
        window.history.length ? this.$router.back() : this.$router.push({ name: 'home' })
      }
    },
    inputFormField (fields) {
      if (fields.employment_type.value && fields.employment_type.value.name === 'partner') {
        fields.partner.hidden = false
      } else {
        fields.partner.hidden = true
      }
    },

    submit () {
      if (this.userSkills.some(item => !item.skill || !item.scale)) {
        this.showSkillsError = true
      } else {
        this.showSkillsError = false
      }

      this.userContactDetailsData.isSubmitted = false
      this.createProfileData.isSubmitted = false

      this.$refs.contactDetailsForm.submit()
      this.$refs.form.submit()

      if (!this.userContactDetailsData.isSubmitted || !this.createProfileData.isSubmitted || this.showSkillsError) {
        return
      }

      const userData = {
        ...this.userCheckBoxes.form,
        ...this.createProfileData.data,
        ...this.userContactDetailsData.data,
        skills: { name: 'skills', value: [...this.userSkills.map(item => ({ id: item.skill.id, name: item.skill.name, value: item.scale }))] }
      }

      const formData = this.prepareDataForRequest(userData)
      formData.avatar = this.userAvatar
      this.loading = true
      this.$emit('loadingChange', true)

      this.axios.post(
        `/api/users/profiles`,
        formData,
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        }).then(response => {
        if (response.status === 200) {
          this.$snotify.success('User profile successfully created', 'Success', this.$store.state.snotify)
        }
        this.onClose()
      }).catch(err => {
        this.errorNotifications(err)
      }).finally(() => {
        this.loading = false
        this.$emit('loadingChange', false)
      })
    },
    userCheckBoxesHandler (name, { value }) {
      this.userCheckBoxes.form[name].value = value
    },
    async uploadCroppedAvatar (data) {
      this.userAvatar = await this.fileToBase64(data)
      this.createProfile.showAvatarCropModal = false
    }
  }

}
</script>
